<template>
  <UsuariosExtLayout
    title="Comprar tarjeta"
    subtitle="Solicita tu tarjeta Conecta Sabana"
    description="Completa tus datos, elige cómo recibir tu tarjeta y realiza tu primera recarga."
    :show-back="true"
    narrow
  >
    <loading v-if="cargando" />

    <div class="conecta-form-card">
      <form-wizard
        ref="wizard"
        title="Comprar tarjeta"
        subtitle="Solicita tu tarjeta Conecta Sabana"
        @on-complete="submitCompra"
        @on-change="onWizardChange"
        color="#05A85A"
        back-button-text="Anterior"
        next-button-text="Siguiente"
        finish-button-text="Pagar ahora"
      >
        <!-- Paso 1: Datos del usuario -->
        <tab-content title="Tus datos" :before-change="validateStep1" icon="fas fa-user">
          <p class="compra-tarjeta__step-hint">
            <i class="fas fa-info-circle mr-1"></i>
            Ingresa la información personal que quedará asociada a tu tarjeta.
          </p>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="tipoDocumento" class="conecta-form-label">
                <i class="fas fa-id-card"></i>
                Tipo de Documento <span class="text-danger">*</span>
              </label>
              <select
                class="form-control conecta-form-control"
                id="tipoDocumento"
                v-model="formData.tipoDocumento"
                :class="{ 'is-invalid': errors.tipoDocumento }"
              >
                <option value="">Seleccione un tipo</option>
                <option v-for="tipo in tiposDocumentos" :key="tipo.id" :value="tipo.numeracion">
                  {{ tipo.descripcion }}
                </option>
              </select>
              <div v-if="errors.tipoDocumento" class="conecta-invalid-feedback">
                {{ errors.tipoDocumento }}
              </div>
            </div>

            <div class="col-md-6 mb-3">
              <label for="documento" class="conecta-form-label">
                <i class="fas fa-id-card"></i>
                Número de Documento <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control conecta-form-control"
                id="documento"
                v-model="formData.documento"
                :class="{ 'is-invalid': errors.documento }"
                placeholder="Número de documento"
              />
              <div v-if="errors.documento" class="conecta-invalid-feedback">
                {{ errors.documento }}
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="nombres" class="conecta-form-label">
                <i class="fas fa-user"></i>
                Nombres <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control conecta-form-control"
                id="nombres"
                v-model="formData.nombres"
                :class="{ 'is-invalid': errors.nombres }"
                placeholder="Sus nombres"
              />
              <div v-if="errors.nombres" class="conecta-invalid-feedback">
                {{ errors.nombres }}
              </div>
            </div>

            <div class="col-md-6 mb-3">
              <label for="apellidos" class="conecta-form-label">
                <i class="fas fa-user"></i>
                Apellidos <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control conecta-form-control"
                id="apellidos"
                v-model="formData.apellidos"
                :class="{ 'is-invalid': errors.apellidos }"
                placeholder="Sus apellidos"
              />
              <div v-if="errors.apellidos" class="conecta-invalid-feedback">
                {{ errors.apellidos }}
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="email" class="conecta-form-label">
                <i class="fas fa-envelope"></i>
                Email <span class="text-danger">*</span>
              </label>
              <input
                type="email"
                class="form-control conecta-form-control"
                id="email"
                v-model="formData.email"
                :class="{ 'is-invalid': errors.email }"
                placeholder="correo@ejemplo.com"
              />
              <div v-if="errors.email" class="conecta-invalid-feedback">
                {{ errors.email }}
              </div>
            </div>

            <div class="col-md-6 mb-3">
              <label for="telefono" class="conecta-form-label">
                <i class="fas fa-phone"></i>
                Teléfono <span class="text-danger">*</span>
              </label>
              <input
                type="tel"
                class="form-control conecta-form-control"
                id="telefono"
                v-model="formData.telefono"
                :class="{ 'is-invalid': errors.telefono }"
                placeholder="300 1234567"
              />
              <div v-if="errors.telefono" class="conecta-invalid-feedback">
                {{ errors.telefono }}
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label for="municipio" class="conecta-form-label">
                <i class="fas fa-city"></i>
                Municipio <span class="text-danger">*</span>
              </label>
              <v-select
                v-model="municipio"
                :options="ciudades"
                label="nombre"
                :class="{ 'is-invalid': errors.municipio }"
                placeholder="Seleccione un municipio"
                @input="selectMunicipio"
              >
                <template slot="no-options">
                  <span>No hay municipios disponibles</span>
                </template>
              </v-select>
              <div v-if="errors.municipio" class="conecta-invalid-feedback">
                {{ errors.municipio }}
              </div>
            </div>

            <div class="col-md-6 mb-3">
              <label for="direccion" class="conecta-form-label">
                <i class="fas fa-map-marker-alt"></i>
                Dirección <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control conecta-form-control"
                id="direccion"
                v-model="formData.direccion"
                :class="{ 'is-invalid': errors.direccion }"
                placeholder="Dirección completa"
              />
              <div v-if="errors.direccion" class="conecta-invalid-feedback">
                {{ errors.direccion }}
              </div>
            </div>
          </div>
        </tab-content>

        <!-- Paso 2: Compra, recarga y entrega -->
        <tab-content title="Compra y entrega" :before-change="validateStep2" icon="fas fa-shopping-cart">
          <p class="compra-tarjeta__step-hint">
            <i class="fas fa-info-circle mr-1"></i>
            Define el valor de tu tarjeta, tu recarga inicial y cómo deseas recibirla.
          </p>

          <!-- Valores -->
          <div class="compra-tarjeta__valores mb-4">
            <div class="compra-tarjeta__valor-fijo">
              <span class="compra-tarjeta__valor-label">
                <i class="fas fa-credit-card mr-2"></i>Valor de la tarjeta
              </span>
              <span class="compra-tarjeta__valor-monto">{{ formatoPesos(VALOR_TARJETA) }}</span>
            </div>

            <div class="mb-3">
              <label for="recargaInicial" class="conecta-form-label">
                <i class="fas fa-wallet"></i>
                Recarga inicial <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="recargaInicial"
                v-model="formData.recarga_inicial"
                v-only-numbers
                maxlength="6"
                class="form-control conecta-form-control"
                :class="{ 'is-invalid': errors.recarga_inicial }"
                placeholder="Ej: 10000"
                @input="onRecargaInput"
              />
              <small class="conecta-form-hint d-block">
                Mínimo {{ formatoPesos(RECARGA_MINIMA) }} · Máximo {{ formatoPesos(RECARGA_MAXIMA) }}
              </small>
              <div v-if="errors.recarga_inicial" class="conecta-invalid-feedback">
                {{ errors.recarga_inicial }}
              </div>
            </div>
          </div>

          <!-- Tipo de entrega -->
          <div class="mb-3">
            <label class="conecta-form-label d-block mb-2">
              <i class="fas fa-truck"></i>
              Tipo de entrega <span class="text-danger">*</span>
            </label>
            <div class="row">
              <div class="col-md-6 mb-2">
                <button
                  type="button"
                  class="compra-tarjeta__opcion-entrega"
                  :class="{ 'compra-tarjeta__opcion-entrega--active': formData.tipo_entrega === 'domicilio' }"
                  @click="seleccionarEntrega('domicilio')"
                >
                  <i class="fas fa-home compra-tarjeta__opcion-icon"></i>
                  <span class="compra-tarjeta__opcion-title">Domicilio</span>
                  <span class="compra-tarjeta__opcion-desc">Indica la dirección de envío</span>
                </button>
              </div>
              <div class="col-md-6 mb-2">
                <button
                  type="button"
                  class="compra-tarjeta__opcion-entrega"
                  :class="{ 'compra-tarjeta__opcion-entrega--active': formData.tipo_entrega === 'recoger_punto' }"
                  @click="seleccionarEntrega('recoger_punto')"
                >
                  <i class="fas fa-map-marker-alt compra-tarjeta__opcion-icon"></i>
                  <span class="compra-tarjeta__opcion-title">Recoger en punto</span>
                  <span class="compra-tarjeta__opcion-desc">Retira en un punto autorizado</span>
                </button>
              </div>
            </div>
            <div v-if="errors.tipo_entrega" class="conecta-invalid-feedback">
              {{ errors.tipo_entrega }}
            </div>
          </div>

          <!-- Domicilio: dirección de entrega + mapa -->
          <div v-if="formData.tipo_entrega === 'domicilio'" class="mb-3">
            <div class="compra-tarjeta__info-box mb-3">
              <i class="fas fa-shipping-fast mr-2"></i>
              Indica la dirección donde deseas recibir tu tarjeta.
              <span v-if="municipio">Municipio: <strong>{{ municipio.nombre }}</strong></span>
            </div>

            <div class="compra-tarjeta__ubicacion-tabs mb-3">
              <button
                type="button"
                class="compra-tarjeta__ubicacion-tab"
                :class="{ 'compra-tarjeta__ubicacion-tab--active': modoUbicacion === 'buscar' }"
                @click="setModoUbicacion('buscar')"
              >
                <i class="fas fa-search-location mr-1"></i>
                Buscar dirección
              </button>
              <button
                type="button"
                class="compra-tarjeta__ubicacion-tab"
                :class="{ 'compra-tarjeta__ubicacion-tab--active': modoUbicacion === 'mapa' }"
                @click="setModoUbicacion('mapa')"
              >
                <i class="fas fa-map-marked-alt mr-1"></i>
                Seleccionar en el mapa
              </button>
            </div>

            <div class="row">
              <div class="col-12 mb-3" v-show="modoUbicacion === 'buscar'">
                <label class="conecta-form-label">
                  <i class="fas fa-map-marker-alt"></i>
                  Buscar dirección en Google <span class="text-danger">*</span>
                </label>
                <div
                  ref="placesAutocompleteHost"
                  class="compra-tarjeta__places-host"
                  :class="{ 'is-invalid': errors.direccion_entrega || errors.ubicacion }"
                ></div>
                <small class="conecta-form-hint d-block mt-1">
                  Escribe y selecciona una sugerencia para ubicar el marcador automáticamente.
                </small>
                <div v-if="errors.places" class="conecta-invalid-feedback d-block">
                  {{ errors.places }}
                </div>
              </div>

              <div class="col-12 mb-3" v-show="modoUbicacion === 'mapa'">
                <div class="compra-tarjeta__mapa-hint">
                  <i class="fas fa-hand-pointer mr-2"></i>
                  Toca el mapa para colocar el pin o arrástralo para corregir la ubicación.
                  La dirección se actualizará automáticamente.
                </div>
              </div>

              <div class="col-md-12 mb-3">
                <label for="direccionEntrega" class="conecta-form-label">
                  <i class="fas fa-home"></i>
                  Dirección confirmada <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control conecta-form-control"
                  id="direccionEntrega"
                  v-model="formData.direccion_entrega"
                  :class="{ 'is-invalid': errors.direccion_entrega }"
                  placeholder="Dirección de entrega"
                  @input="onDireccionManualInput"
                />
                <div v-if="errors.direccion_entrega" class="conecta-invalid-feedback">
                  {{ errors.direccion_entrega }}
                </div>
              </div>

              <div class="col-md-12 mb-3">
                <label for="complementoEntrega" class="conecta-form-label">
                  <i class="fas fa-building"></i>
                  Complemento de dirección
                </label>
                <input
                  type="text"
                  class="form-control conecta-form-control"
                  id="complementoEntrega"
                  v-model="formData.complemento_entrega"
                  :class="{ 'is-invalid': errors.complemento_entrega }"
                  placeholder="Ej: Apto 301, Torre B, Barrio Centro"
                />
                <small class="conecta-form-hint d-block">
                  Opcional: apartamento, torre, barrio u otras referencias.
                </small>
                <div v-if="errors.complemento_entrega" class="conecta-invalid-feedback">
                  {{ errors.complemento_entrega }}
                </div>
              </div>

              <div class="col-12 mb-2">
                <div class="compra-tarjeta__mapa-wrap">
                  <div class="compra-tarjeta__mapa-header">
                    <span>
                      <i class="fas fa-map mr-1"></i>
                      Ubicación en el mapa
                    </span>
                    <span
                      v-if="tieneUbicacion"
                      class="compra-tarjeta__coords badge badge-success"
                    >
                      {{ Number(formData.latitud_entrega).toFixed(5) }},
                      {{ Number(formData.longitud_entrega).toFixed(5) }}
                    </span>
                    <span v-else class="compra-tarjeta__coords badge badge-warning">
                      Sin ubicación seleccionada
                    </span>
                  </div>
                  <gmap-map
                    :center="mapCenter"
                    :zoom="mapZoom"
                    map-type-id="roadmap"
                    class="compra-tarjeta__mapa"
                    :options="mapOptions"
                    @click="onMapClick"
                  >
                    <gmap-marker
                      v-if="markerPosition"
                      :position="markerPosition"
                      :draggable="true"
                      @dragend="onMarkerDragEnd"
                    />
                  </gmap-map>
                </div>
                <div v-if="errors.ubicacion" class="conecta-invalid-feedback d-block mt-1">
                  {{ errors.ubicacion }}
                </div>
              </div>
            </div>
          </div>

          <!-- Puntos de recogida -->
          <div v-if="formData.tipo_entrega === 'recoger_punto'" class="mb-3">
            <label class="conecta-form-label d-block mb-2">
              <i class="fas fa-store"></i>
              Puntos de emisión – Conecta Sabana <span class="text-danger">*</span>
            </label>
            <small class="conecta-form-hint d-block mb-2">
              Seleccione el punto donde desea recoger su tarjeta.
            </small>

            <div v-if="sitios.length === 0" class="compra-tarjeta__info-box">
              <i class="fas fa-exclamation-circle mr-2"></i>
              No hay puntos de recogida disponibles en este momento.
            </div>

            <div v-else class="compra-tarjeta__sitios-list">
              <button
                v-for="sitio in sitios"
                :key="sitio.id"
                type="button"
                class="compra-tarjeta__sitio-item"
                :class="{ 'compra-tarjeta__sitio-item--active': formData.sitio_id === sitio.id }"
                @click="seleccionarSitio(sitio)"
              >
                <div class="compra-tarjeta__sitio-check">
                  <i
                    :class="formData.sitio_id === sitio.id ? 'fas fa-check-circle' : 'far fa-circle'"
                  ></i>
                </div>
                <div class="compra-tarjeta__sitio-info">
                  <strong>{{ sitio.nombre }}</strong>
                  <p v-if="sitio.direccion" class="mb-1 small text-muted">
                    <i class="fas fa-map-marker-alt mr-1"></i>{{ sitio.direccion }}
                  </p>
                  <p
                    v-for="(horario, idx) in sitio.horarios"
                    :key="sitio.id + '-h-' + idx"
                    class="mb-1 small text-muted"
                  >
                    <i class="fas fa-clock mr-1"></i>{{ horario }}
                  </p>
                </div>
              </button>
            </div>

            <div v-if="errors.sitio_id" class="conecta-invalid-feedback">
              {{ errors.sitio_id }}
            </div>
          </div>

          <!-- Resumen parcial de valores -->
          <div class="compra-tarjeta__resumen">
            <h6 class="compra-tarjeta__resumen-title">
              <i class="fas fa-receipt mr-2"></i>Resumen de pago
            </h6>
            <div class="compra-tarjeta__resumen-row">
              <span>Valor tarjeta</span>
              <span>{{ formatoPesos(VALOR_TARJETA) }}</span>
            </div>
            <div class="compra-tarjeta__resumen-row">
              <span>Recarga inicial</span>
              <span>{{ formatoPesos(recargaNumero) }}</span>
            </div>
            <div class="compra-tarjeta__resumen-row compra-tarjeta__resumen-total">
              <span>Total a pagar</span>
              <span>{{ formatoPesos(totalPagar) }}</span>
            </div>
          </div>
        </tab-content>

        <!-- Paso 3: Resumen de compra -->
        <tab-content title="Resumen de compra" icon="fas fa-clipboard-check">
          <p class="compra-tarjeta__step-hint">
            <i class="fas fa-info-circle mr-1"></i>
            Verifica la información antes de continuar al pago seguro con ePayco.
          </p>

          <div class="compra-tarjeta__resumen-final">
            <section class="compra-tarjeta__bloque">
              <h6 class="compra-tarjeta__bloque-title">
                <i class="fas fa-user mr-2"></i>Datos del comprador
              </h6>
              <div class="compra-tarjeta__resumen-row">
                <span>Nombre</span>
                <span>{{ formData.nombres }} {{ formData.apellidos }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Documento</span>
                <span>{{ tipoDocumentoLabel }} {{ formData.documento }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Email</span>
                <span>{{ formData.email }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Teléfono</span>
                <span>{{ formData.telefono }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Municipio</span>
                <span>{{ municipio ? municipio.nombre : "—" }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Dirección</span>
                <span>{{ formData.direccion }}</span>
              </div>
            </section>

            <section class="compra-tarjeta__bloque">
              <h6 class="compra-tarjeta__bloque-title">
                <i class="fas fa-truck mr-2"></i>Entrega
              </h6>
              <div class="compra-tarjeta__resumen-row">
                <span>Tipo</span>
                <span>{{ tipoEntregaLabel }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Detalle</span>
                <span>{{ detalleEntrega || "—" }}</span>
              </div>
              <div
                v-if="formData.tipo_entrega === 'domicilio' && tieneUbicacion"
                class="compra-tarjeta__resumen-row"
              >
                <span>Ubicación</span>
                <span class="small">
                  {{ Number(formData.latitud_entrega).toFixed(5) }},
                  {{ Number(formData.longitud_entrega).toFixed(5) }}
                </span>
              </div>
            </section>

            <section class="compra-tarjeta__bloque">
              <h6 class="compra-tarjeta__bloque-title">
                <i class="fas fa-receipt mr-2"></i>Valores
              </h6>
              <div class="compra-tarjeta__resumen-row">
                <span>Valor tarjeta</span>
                <span>{{ formatoPesos(VALOR_TARJETA) }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row">
                <span>Recarga inicial</span>
                <span>{{ formatoPesos(recargaNumero) }}</span>
              </div>
              <div class="compra-tarjeta__resumen-row compra-tarjeta__resumen-total">
                <span>Total a pagar</span>
                <span>{{ formatoPesos(totalPagar) }}</span>
              </div>
            </section>

            <div class="compra-tarjeta__info-box mt-3 mb-0">
              <i class="fas fa-lock mr-2"></i>
              Al continuar serás redirigido a la pasarela de pagos de ePayco para completar la compra de forma segura.
            </div>
          </div>

          <div class="mb-3 mt-4">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="autorizacion-compra"
                v-model="formData.autorizacion"
                :class="{ 'is-invalid': errors.autorizacion }"
              />
              <label class="form-check-label conecta-form-check-label" for="autorizacion-compra">
                <i class="fas fa-shield-alt mr-1"></i>
                <a href="/tratamiento-datos-personales" target="_blank">
                  Autorizo el tratamiento de mis datos personales
                </a>
                <span class="text-danger">*</span>
              </label>
              <div v-if="errors.autorizacion" class="conecta-invalid-feedback">
                <i class="fas fa-exclamation-circle mr-1"></i>
                {{ errors.autorizacion }}
              </div>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="terminos-compra"
                v-model="formData.terminos"
                :class="{ 'is-invalid': errors.terminos }"
              />
              <label class="form-check-label conecta-form-check-label" for="terminos-compra">
                <i class="fas fa-file-contract mr-1"></i>
                <a href="/terminos-condiciones-tarjetas" target="_blank">
                  Acepto los términos y condiciones
                </a>
                <span class="text-danger">*</span>
              </label>
              <div v-if="errors.terminos" class="conecta-invalid-feedback">
                <i class="fas fa-exclamation-circle mr-1"></i>
                {{ errors.terminos }}
              </div>
            </div>
          </div>

          <div class="mb-3 mt-4">
            <div class="d-flex justify-content-center">
              <div id="recaptcha-container-compra"></div>
            </div>
            <div v-if="errors.recaptcha" class="conecta-invalid-feedback text-center mt-2">
              <i class="fas fa-exclamation-circle mr-1"></i>
              {{ errors.recaptcha }}
            </div>
          </div>
        </tab-content>
      </form-wizard>

      <div class="d-flex justify-content-center mt-3">
        <button type="button" class="btn conecta-btn-outline px-4 py-2" @click="resetForm">
          <i class="fas fa-undo mr-2"></i>
          Limpiar formulario
        </button>
      </div>
    </div>
  </UsuariosExtLayout>
</template>

<script>
import axios from "axios";
import UsuariosExtLayout from "../../components/usuariosExt/UsuariosExtLayout.vue";
import Loading from "../../components/LoadingSpinner";

const VALOR_TARJETA = 8000;
const RECARGA_MINIMA = 10000;
const RECARGA_MAXIMA = 300000;

export default {
  name: "CompraTarjeta",

  components: {
    UsuariosExtLayout,
    Loading,
  },

  data() {
    return {
      VALOR_TARJETA,
      RECARGA_MINIMA,
      RECARGA_MAXIMA,
      formData: {
        tipoDocumento: "",
        documento: "",
        nombres: "",
        apellidos: "",
        email: "",
        telefono: "",
        direccion: "",
        municipio_id: null,
        recarga_inicial: "",
        tipo_entrega: "",
        sitio_id: null,
        direccion_entrega: "",
        complemento_entrega: "",
        latitud_entrega: null,
        longitud_entrega: null,
        autorizacion: false,
        terminos: false,
      },
      tiposDocumentos: [],
      ciudades: [],
      municipio: null,
      modoUbicacion: "buscar",
      mapZoom: 14,
      mapCenterDefault: { lat: 4.9186, lng: -74.028 },
      mapOptions: {
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        clickableIcons: false,
      },
      placeAutocompleteEl: null,
      sitios: [
        {
          id: 1,
          nombre: "TABIO",
          direccion: "Diagonal 7 # 1-17 Local 10",
          horarios: [
            "Lunes a Sábado: 8:00 a.m. – 7:50 p.m.",
            "Domingo y Festivos: 8:00 a.m. – 3:00 p.m.",
          ],
        },
        {
          id: 2,
          nombre: "TENJO",
          direccion: "Carrera 2 # 4-55 Local 114 – Centro Comercial Achua",
          horarios: [
            "Lunes a Sábado: 8:00 a.m. – 7:00 p.m.",
            "Domingo y Festivos: 8:00 a.m. – 3:00 p.m.",
          ],
        },
        {
          id: 3,
          nombre: "CAJICÁ",
          direccion: "Diagonal 1 # 14-31",
          horarios: [
            "Lunes a Sábado: 8:00 a.m. – 12:00 m.",
            "Domingo y Festivos: 8:00 a.m. – 3:00 p.m.",
          ],
        },
        {
          id: 4,
          nombre: "SOPÓ",
          direccion: "Carrera 3 # 1-35",
          horarios: [
            "Lunes a Sábado: 8:00 a.m. – 8:00 p.m.",
            "Domingo y Festivos: 8:30 a.m. – 6:00 p.m.",
          ],
        },
      ],
      errors: {},
      cargando: false,
      referencia: "",
      recaptchaToken: null,
      recaptchaWidgetId: null,
      currentStep: 0,
    };
  },

  computed: {
    recargaNumero() {
      return Number(String(this.formData.recarga_inicial || "").replace(/\D/g, "")) || 0;
    },
    totalPagar() {
      return VALOR_TARJETA + this.recargaNumero;
    },
    formatoPesos() {
      return (valor) =>
        Number(valor || 0).toLocaleString("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
    },
    tipoDocumentoLabel() {
      const tipo = this.tiposDocumentos.find(
        (t) => String(t.numeracion) === String(this.formData.tipoDocumento)
      );
      return tipo ? tipo.descripcion : "";
    },
    sitioSeleccionado() {
      return this.sitios.find((s) => s.id === this.formData.sitio_id) || null;
    },
    tipoEntregaLabel() {
      if (this.formData.tipo_entrega === "domicilio") return "Domicilio";
      if (this.formData.tipo_entrega === "recoger_punto") return "Recoger en punto";
      return "—";
    },
    tieneUbicacion() {
      const lat = Number(this.formData.latitud_entrega);
      const lng = Number(this.formData.longitud_entrega);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },
    markerPosition() {
      if (!this.tieneUbicacion) return null;
      return {
        lat: Number(this.formData.latitud_entrega),
        lng: Number(this.formData.longitud_entrega),
      };
    },
    mapCenter() {
      if (this.markerPosition.lat && this.markerPosition.lng) return this.markerPosition;
      if (this.municipio && this.municipio.latitud && this.municipio.longitud) {
        return {
          lat: Number(this.municipio.latitud),
          lng: Number(this.municipio.longitud),
        };
      }
      return this.mapCenterDefault;
    },
    detalleEntrega() {
      if (this.formData.tipo_entrega === "domicilio") {
        const complemento = this.formData.complemento_entrega
          ? `, ${this.formData.complemento_entrega}`
          : "";
        return `${this.formData.direccion_entrega || ""}${complemento}`.trim();
      }
      if (this.formData.tipo_entrega === "recoger_punto" && this.sitioSeleccionado) {
        return `${this.sitioSeleccionado.nombre} — ${this.sitioSeleccionado.direccion}`;
      }
      return "";
    },
  },

  mounted() {
    this.getCiudades();
    this.getTiposDocumentos();
    this.loadRecaptcha();

    if (typeof window.ePayco === "undefined") {
      const script = document.createElement("script");
      script.src = "https://checkout.epayco.co/checkout.js";
      script.async = true;
      document.head.appendChild(script);
    }
  },

  beforeDestroy() {
    this.destroyPlacesAutocomplete();
    if (this.recaptchaWidgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      } catch (e) {
        console.log("Error al resetear reCAPTCHA:", e);
      }
    }
  },

  methods: {
    onWizardChange(prevIndex, nextIndex) {
      this.currentStep = nextIndex;
      if (nextIndex === 2) {
        this.$nextTick(() => {
          if (this.recaptchaWidgetId === null) {
            this.renderRecaptcha();
          }
        });
      }
    },
    seleccionarEntrega(tipo) {
      this.formData.tipo_entrega = tipo;
      this.errors.tipo_entrega = null;

      if (tipo === "domicilio") {
        this.formData.sitio_id = null;
        this.errors.sitio_id = null;
        this.modoUbicacion = "buscar";
        if (!this.formData.direccion_entrega && this.formData.direccion) {
          this.formData.direccion_entrega = this.formData.direccion;
        }
        this.$nextTick(() => {
          this.centrarMapaMunicipio();
          this.initPlacesAutocompleteNew();
        });
      } else {
        this.destroyPlacesAutocomplete();
        this.errors.direccion_entrega = null;
        this.errors.complemento_entrega = null;
        this.errors.ubicacion = null;
        this.errors.places = null;
        this.formData.latitud_entrega = null;
        this.formData.longitud_entrega = null;
      }
    },

    setModoUbicacion(modo) {
      this.modoUbicacion = modo;
      this.errors.ubicacion = null;
      if (modo === "mapa" && !this.tieneUbicacion) {
        this.centrarMapaMunicipio();
      }
      if (modo === "buscar") {
        this.$nextTick(() => this.initPlacesAutocompleteNew());
      }
    },

    async initPlacesAutocompleteNew() {
      if (this.placeAutocompleteEl) return;
      const host = this.$refs.placesAutocompleteHost;
      if (!host) return;

      try {
        if (!window.google || !window.google.maps) {
          this.errors.places = "Google Maps aún no está listo. Intente de nuevo en unos segundos.";
          return;
        }

        const placesLib = await window.google.maps.importLibrary("places");
        const PlaceAutocompleteElement = placesLib.PlaceAutocompleteElement;

        if (!PlaceAutocompleteElement) {
          this.errors.places =
            "Places API (New) no está disponible. Active Places API (New) en Google Cloud.";
          return;
        }

        const placeAutocomplete = new PlaceAutocompleteElement({
          includedRegionCodes: ["co"],
        });
        placeAutocomplete.id = "compra-place-autocomplete";
        placeAutocomplete.classList.add("compra-tarjeta__place-el");

        host.innerHTML = "";
        host.appendChild(placeAutocomplete);
        this.placeAutocompleteEl = placeAutocomplete;
        this.errors.places = null;

        placeAutocomplete.addEventListener("gmp-select", async (event) => {
          try {
            const prediction = event.placePrediction;
            if (!prediction) return;

            const place = prediction.toPlace();
            await place.fetchFields({
              fields: ["formattedAddress", "location", "displayName"],
            });

            const loc = place.location;
            if (!loc) return;

            const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
            const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
            const address =
              place.formattedAddress ||
              place.displayName ||
              this.formData.direccion_entrega;

            this.setUbicacion(lat, lng, address);
          } catch (err) {
            console.error("Error al obtener el lugar seleccionado:", err);
            this.errors.places = "No se pudo obtener la ubicación seleccionada.";
          }
        });
      } catch (err) {
        console.error("Error Places API (New):", err);
        this.errors.places =
          "No se pudo cargar el buscador de direcciones. Verifique que Places API (New) esté activa.";
      }
    },

    destroyPlacesAutocomplete() {
      if (this.placeAutocompleteEl) {
        try {
          this.placeAutocompleteEl.remove();
        } catch (e) {
          /* ignore */
        }
        this.placeAutocompleteEl = null;
      }
      const host = this.$refs.placesAutocompleteHost;
      if (host) host.innerHTML = "";
    },

    centrarMapaMunicipio() {
      if (this.municipio && this.municipio.latitud && this.municipio.longitud) {
        this.mapZoom = 14;
      } else {
        this.mapZoom = 12;
      }
    },

    setUbicacion(lat, lng, direccionOpcional) {
      this.formData.latitud_entrega = Number(lat);
      this.formData.longitud_entrega = Number(lng);
      this.mapZoom = 17;
      this.errors.ubicacion = null;
      if (direccionOpcional) {
        this.formData.direccion_entrega = direccionOpcional;
        this.errors.direccion_entrega = null;
      }
    },

    onMapClick(event) {
      if (!event || !event.latLng) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.setUbicacion(lat, lng);
      this.reverseGeocode(lat, lng);
    },

    onMarkerDragEnd(event) {
      if (!event || !event.latLng) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.setUbicacion(lat, lng);
      this.reverseGeocode(lat, lng);
    },

    reverseGeocode(lat, lng) {
      if (!window.google || !window.google.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          this.formData.direccion_entrega = results[0].formatted_address;
          this.errors.direccion_entrega = null;
        }
      });
    },

    onDireccionManualInput() {
      this.errors.direccion_entrega = null;
    },

    seleccionarSitio(sitio) {
      this.formData.sitio_id = sitio.id;
      this.errors.sitio_id = null;
    },

    onRecargaInput() {
      const digits = String(this.formData.recarga_inicial || "").replace(/\D/g, "");
      const n = Number(digits || 0);
      this.formData.recarga_inicial = n ? String(Math.min(n, RECARGA_MAXIMA)) : "";
    },

    selectMunicipio() {
      if (this.municipio && this.municipio.id) {
        this.formData.municipio_id = this.municipio.id;
        this.errors.municipio = null;
        this.centrarMapaMunicipio();
      } else {
        this.formData.municipio_id = null;
      }
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    validateStep1() {
      this.errors = {};
      let isValid = true;

      if (!this.formData.tipoDocumento) {
        this.errors.tipoDocumento = "Seleccione el tipo de documento.";
        isValid = false;
      }
      if (!this.formData.documento.trim()) {
        this.errors.documento = "Ingrese su número de documento.";
        isValid = false;
      } else if (this.formData.documento.trim().length < 5) {
        this.errors.documento = "El documento debe tener al menos 5 caracteres.";
        isValid = false;
      }
      if (!this.formData.nombres.trim()) {
        this.errors.nombres = "Ingrese sus nombres.";
        isValid = false;
      } else if (this.formData.nombres.trim().length < 2) {
        this.errors.nombres = "Los nombres deben tener al menos 2 caracteres.";
        isValid = false;
      }
      if (!this.formData.apellidos.trim()) {
        this.errors.apellidos = "Ingrese sus apellidos.";
        isValid = false;
      } else if (this.formData.apellidos.trim().length < 2) {
        this.errors.apellidos = "Los apellidos deben tener al menos 2 caracteres.";
        isValid = false;
      }
      if (!this.formData.email.trim()) {
        this.errors.email = "Ingrese su correo electrónico.";
        isValid = false;
      } else if (!this.isValidEmail(this.formData.email)) {
        this.errors.email = "Ingrese un correo válido.";
        isValid = false;
      }
      if (!this.formData.telefono.trim()) {
        this.errors.telefono = "Ingrese su teléfono.";
        isValid = false;
      } else if (this.formData.telefono.trim().length < 7) {
        this.errors.telefono = "El teléfono debe tener al menos 7 dígitos.";
        isValid = false;
      }
      if (!this.municipio || !this.municipio.id) {
        this.errors.municipio = "Seleccione un municipio.";
        isValid = false;
      }
      if (!this.formData.direccion.trim()) {
        this.errors.direccion = "Ingrese su dirección.";
        isValid = false;
      } else if (this.formData.direccion.trim().length < 10) {
        this.errors.direccion = "La dirección debe tener al menos 10 caracteres.";
        isValid = false;
      }

      if (!isValid) {
        this.$swal.fire({
          icon: "warning",
          title: "Datos incompletos",
          text: "Revise los campos marcados antes de continuar.",
        });
      }

      return isValid;
    },

    validateStep2() {
      this.errors = {};
      let isValid = true;

      if (!this.recargaNumero) {
        this.errors.recarga_inicial = "Ingrese el valor de la recarga inicial.";
        isValid = false;
      } else if (this.recargaNumero < RECARGA_MINIMA) {
        this.errors.recarga_inicial = `La recarga mínima es ${this.formatoPesos(RECARGA_MINIMA)}.`;
        isValid = false;
      } else if (this.recargaNumero > RECARGA_MAXIMA) {
        this.errors.recarga_inicial = `La recarga máxima es ${this.formatoPesos(RECARGA_MAXIMA)}.`;
        isValid = false;
      }

      if (!this.formData.tipo_entrega) {
        this.errors.tipo_entrega = "Seleccione un tipo de entrega.";
        isValid = false;
      }

      if (this.formData.tipo_entrega === "domicilio") {
        if (!this.formData.direccion_entrega || !this.formData.direccion_entrega.trim()) {
          this.errors.direccion_entrega = "Ingrese o seleccione la dirección de entrega.";
          isValid = false;
        } else if (this.formData.direccion_entrega.trim().length < 10) {
          this.errors.direccion_entrega = "La dirección de entrega debe tener al menos 10 caracteres.";
          isValid = false;
        }
        if (!this.tieneUbicacion) {
          this.errors.ubicacion =
            "Seleccione la ubicación en el mapa o busque la dirección en Google para obtener coordenadas.";
          isValid = false;
        }
      }

      if (this.formData.tipo_entrega === "recoger_punto" && !this.formData.sitio_id) {
        this.errors.sitio_id = "Seleccione un punto de recogida.";
        isValid = false;
      }

      if (!isValid) {
        this.$swal.fire({
          icon: "warning",
          title: "Información pendiente",
          text: "Complete la compra y el tipo de entrega antes de continuar.",
        });
      }

      return isValid;
    },

    async submitCompra() {
      if (!this.validateStep1() || !this.validateStep2()) return;

      this.errors.autorizacion = null;
      this.errors.terminos = null;
      this.errors.recaptcha = null;

      let autorizacionesValidas = true;

      if (!this.formData.autorizacion) {
        this.errors.autorizacion =
          "Debe marcar la casilla para autorizar el tratamiento de sus datos personales. Esto es obligatorio para continuar.";
        autorizacionesValidas = false;
      }

      if (!this.formData.terminos) {
        this.errors.terminos =
          "Debe aceptar los términos y condiciones marcando la casilla correspondiente. Esto es obligatorio para continuar.";
        autorizacionesValidas = false;
      }

      if (!this.recaptchaToken) {
        this.errors.recaptcha = "Por favor, complete la verificación reCAPTCHA para continuar";
        autorizacionesValidas = false;
      }

      if (!autorizacionesValidas) {
        this.$swal.fire({
          icon: "warning",
          title: "Verificación requerida",
          text: "Por favor, complete las autorizaciones y la verificación reCAPTCHA para continuar",
          confirmButtonText: "Entendido",
        });
        return;
      }

      if (typeof window.ePayco === "undefined") {
        this.$swal.fire({
          icon: "warning",
          title: "Pasarela no disponible",
          text: "El script de ePayco aún no se ha cargado. Intente nuevamente en unos segundos.",
        });
        return;
      }

      this.cargando = true;
      this.referencia = "";

      try {
        const payload = {
          ...this.formData,
          municipio: this.municipio ? this.municipio.nombre : null,
          valor_tarjeta: VALOR_TARJETA,
          recarga_inicial: this.recargaNumero,
          total_pagar: this.totalPagar,
          nombre: `${this.formData.nombres} ${this.formData.apellidos}`.trim(),
          no_documento: this.formData.documento,
          email: this.formData.email,
          telefono: this.formData.telefono,
          ciudad: this.municipio ? this.municipio.nombre : "",
          direccion:
            this.formData.tipo_entrega === "domicilio"
              ? this.detalleEntrega
              : this.formData.direccion,
          latitud_entrega:
            this.formData.tipo_entrega === "domicilio"
              ? this.formData.latitud_entrega
              : null,
          longitud_entrega:
            this.formData.tipo_entrega === "domicilio"
              ? this.formData.longitud_entrega
              : null,
          monto: this.totalPagar,
          estado: 1,
          autorizacion: this.formData.autorizacion ? 1 : 0,
          terminos: this.formData.terminos ? 1 : 0,
          recaptcha_token: this.recaptchaToken,
        };

        const response = await axios.post("/api/comprarTarjeta", payload);
        this.referencia = response.data.solicitud_id + "_" + response.data.venta_id || response.data.referencia || "";

        if (!this.referencia) {
          throw new Error("No se generó la referencia de pago");
        }

        if (this.recaptchaWidgetId !== null && window.grecaptcha) {
          window.grecaptcha.reset(this.recaptchaWidgetId);
          this.recaptchaToken = null;
        }

        this.abrirEpaycoCheckout();
      } catch (error) {
        console.error("Error al comprar tarjeta:", error);
        this.$swal.fire({
          icon: "error",
          title: "No se pudo iniciar el pago",
          text: "Intente nuevamente o comuníquese con atención al usuario.",
        });
      } finally {
        this.cargando = false;
      }
    },

    abrirEpaycoCheckout() {
      const handler = window.ePayco.checkout.configure({
        key: process.env.VUE_APP_EPAYCO_PUBLIC_KEY,
        test: false,
      });

      handler.open({
        name: "Compra tarjeta Conecta Sabana",
        description: "Compra de tarjeta + recarga inicial",
        invoice: this.referencia,
        currency: "COP",
        amount: this.totalPagar,
        tax_base: "0",
        tax: "0",
        country: "co",
        lang: "es",
        external: "false",
        extra1: "compra_tarjeta",
        extra2: "no_documento_" + this.formData.documento,
        extra3: "referencia_" + this.referencia,
        response: "https://back.conectasts.com/epayco/respuestaRecarga",
        confirmation: "https://back.conectasts.com/api/comprarTarjeta/webhook",
        method: "POST",
      });
    },

    resetForm() {
      this.formData = {
        tipoDocumento: "",
        documento: "",
        nombres: "",
        apellidos: "",
        email: "",
        telefono: "",
        direccion: "",
        municipio_id: null,
        recarga_inicial: "",
        tipo_entrega: "",
        sitio_id: null,
        direccion_entrega: "",
        complemento_entrega: "",
        latitud_entrega: null,
        longitud_entrega: null,
        autorizacion: false,
        terminos: false,
      };
      this.municipio = null;
      this.modoUbicacion = "buscar";
      this.mapZoom = 14;
      this.destroyPlacesAutocomplete();
      this.errors = {};
      this.referencia = "";
      this.recaptchaToken = null;
      this.currentStep = 0;
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        try {
          window.grecaptcha.reset(this.recaptchaWidgetId);
        } catch (e) {
          console.log("Error al resetear reCAPTCHA:", e);
        }
      }
      this.recaptchaWidgetId = null;
      if (this.$refs.wizard) {
        this.$refs.wizard.reset();
      }
    },

    async getCiudades() {
      try {
        const response = await axios.get("/api/ciudades/lista");
        this.ciudades = response.data;
      } catch (error) {
        console.error("Error al obtener ciudades:", error);
      }
    },

    async getTiposDocumentos() {
      try {
        const response = await axios.get("/api/lista/2");
        this.tiposDocumentos = response.data;
      } catch (error) {
        console.error("Error al obtener tipos de documento:", error);
      }
    },

    loadRecaptcha() {
      if (window.grecaptcha && window.grecaptcha.render) {
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://www.google.com/recaptcha/api.js?onload=vueRecaptchaLoadedCompra&render=explicit";
      script.async = true;
      script.defer = true;

      window.vueRecaptchaLoadedCompra = () => {
        if (this.currentStep === 2) {
          this.renderRecaptcha();
        }
      };

      document.head.appendChild(script);
    },

    renderRecaptcha() {
      this.$nextTick(() => {
        if (window.grecaptcha && document.getElementById("recaptcha-container-compra")) {
          try {
            if (this.recaptchaWidgetId !== null) return;

            this.recaptchaWidgetId = window.grecaptcha.render("recaptcha-container-compra", {
              sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
              callback: this.onRecaptchaSuccess,
              "expired-callback": this.onRecaptchaExpired,
              "error-callback": this.onRecaptchaError,
              theme: "light",
              size: "normal",
            });
          } catch (e) {
            console.error("Error al renderizar reCAPTCHA:", e);
          }
        }
      });
    },

    onRecaptchaSuccess(token) {
      this.recaptchaToken = token;
      this.errors.recaptcha = null;
    },

    onRecaptchaExpired() {
      this.recaptchaToken = null;
      this.errors.recaptcha = "La verificación reCAPTCHA ha expirado. Por favor, vuelva a verificar.";
      if (this.recaptchaWidgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      }
    },

    onRecaptchaError() {
      this.recaptchaToken = null;
      this.errors.recaptcha = "Error en la verificación reCAPTCHA. Por favor, intente nuevamente.";
    },
  },
};
</script>

<style scoped lang="scss">
.compra-tarjeta__step-hint {
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
  color: #3d5c4f;
  background: rgba(5, 168, 90, 0.06);
  border-left: 3px solid #05a85a;
  border-radius: 0 0.5rem 0.5rem 0;
}

.compra-tarjeta__valores {
  padding: 1rem;
  background: #f8fbf9;
  border-radius: 0.75rem;
  border: 1px solid rgba(5, 168, 90, 0.12);
}

.compra-tarjeta__valor-fijo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #05a85a, #61a198);
  border-radius: 0.65rem;
  color: #ffffff;
}

.compra-tarjeta__valor-label {
  font-size: 0.9rem;
  font-weight: 500;
}

.compra-tarjeta__valor-monto {
  font-size: 1.35rem;
  font-weight: 700;
}

.compra-tarjeta__opcion-entrega {
  width: 100%;
  padding: 1rem;
  text-align: left;
  border: 2px solid rgba(5, 168, 90, 0.2);
  border-radius: 0.75rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #05a85a;
    box-shadow: 0 4px 12px rgba(5, 168, 90, 0.12);
  }

  &--active {
    border-color: #05a85a;
    background: rgba(5, 168, 90, 0.06);
    box-shadow: 0 4px 16px rgba(5, 168, 90, 0.15);
  }
}

.compra-tarjeta__opcion-icon {
  display: block;
  font-size: 1.5rem;
  color: #05a85a;
  margin-bottom: 0.5rem;
}

.compra-tarjeta__opcion-title {
  display: block;
  font-weight: 600;
  color: #0d3d2b;
  margin-bottom: 0.15rem;
}

.compra-tarjeta__opcion-desc {
  display: block;
  font-size: 0.8rem;
  color: #6b8a7a;
}

.compra-tarjeta__info-box {
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  color: #3d5c4f;
  background: #e3f1e9;
  border-radius: 0.65rem;
}

.compra-tarjeta__ubicacion-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.compra-tarjeta__ubicacion-tab {
  padding: 0.75rem 0.85rem;
  border: 2px solid rgba(5, 168, 90, 0.2);
  border-radius: 0.65rem;
  background: #ffffff;
  color: #3d5c4f;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #05a85a;
  }

  &--active {
    border-color: #05a85a;
    background: rgba(5, 168, 90, 0.08);
    color: #0d3d2b;
    box-shadow: 0 4px 12px rgba(5, 168, 90, 0.12);
  }
}

.compra-tarjeta__places-host {
  width: 100%;
  min-height: 42px;
}

.compra-tarjeta__places-host.is-invalid {
  outline: 1px solid #dc3545;
  border-radius: 0.35rem;
}

.compra-tarjeta__place-el {
  width: 100%;
}

::v-deep .compra-tarjeta__places-host gmp-place-autocomplete {
  width: 100%;
}

.compra-tarjeta__autocomplete {
  width: 100%;
}

.compra-tarjeta__mapa-hint {
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #5a6b63;
  background: #f7f9f8;
  border-radius: 0.65rem;
  border: 1px dashed rgba(5, 168, 90, 0.35);
}

.compra-tarjeta__mapa-wrap {
  overflow: hidden;
  border: 1px solid rgba(5, 168, 90, 0.2);
  border-radius: 0.85rem;
  box-shadow: 0 8px 24px rgba(13, 61, 43, 0.08);
}

.compra-tarjeta__mapa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.65rem 0.9rem;
  background: linear-gradient(135deg, #0d3d2b, #05a85a);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
}

.compra-tarjeta__coords {
  font-weight: 500;
  font-size: 0.75rem;
}

.compra-tarjeta__mapa {
  width: 100%;
  height: 320px;
}

@media (max-width: 576px) {
  .compra-tarjeta__ubicacion-tabs {
    grid-template-columns: 1fr;
  }

  .compra-tarjeta__mapa {
    height: 260px;
  }
}

.compra-tarjeta__sitios-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 220px;
  overflow-y: auto;
}

.compra-tarjeta__sitio-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  padding: 0.85rem 1rem;
  text-align: left;
  border: 2px solid rgba(5, 168, 90, 0.15);
  border-radius: 0.65rem;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #05a85a;
  }

  &--active {
    border-color: #05a85a;
    background: rgba(5, 168, 90, 0.06);
  }
}

.compra-tarjeta__sitio-check {
  color: #05a85a;
  font-size: 1.1rem;
  padding-top: 0.1rem;
}

.compra-tarjeta__resumen {
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(5, 168, 90, 0.15);
  border-radius: 0.75rem;
}

.compra-tarjeta__resumen-title {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: #0d3d2b;
}

.compra-tarjeta__resumen-row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: #4a6b5c;
}

.compra-tarjeta__resumen-total {
  margin-top: 0.5rem;
  padding-top: 0.65rem;
  border-top: 1px dashed rgba(5, 168, 90, 0.25);
  font-size: 1.05rem;
  font-weight: 700;
  color: #05a85a;
}

.compra-tarjeta__resumen-final {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.compra-tarjeta__bloque {
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(5, 168, 90, 0.15);
  border-radius: 0.75rem;
}

.compra-tarjeta__bloque-title {
  margin: 0 0 0.75rem;
  font-weight: 600;
  color: #0d3d2b;
  font-size: 0.95rem;
}

::v-deep .wizard-nav {
  margin-bottom: 1.5rem;
}

::v-deep .wizard-icon-circle {
  border-color: #05a85a !important;
}

::v-deep .wizard-icon-container {
  background-color: #05a85a !important;
}

::v-deep .stepTitle.active {
  color: #0d3d2b !important;
}

::v-deep .wizard-progress-bar {
  background-color: #05a85a !important;
}

::v-deep .v-select .vs__dropdown-toggle {
  border-radius: 0.65rem;
  border-color: rgba(5, 168, 90, 0.2);
  min-height: 38px;
}

::v-deep .v-select .vs__dropdown-menu {
  background-color: #fff;
}

::v-deep .v-select .vs__dropdown-option--highlight {
  background: #05a85a;
  color: #fff;
}

::v-deep .v-select.is-invalid .vs__dropdown-toggle {
  border-color: #c0392b;
}

#recaptcha-container-compra {
  margin: 1rem 0;
}

@media (max-width: 576px) {
  #recaptcha-container-compra {
    transform: scale(0.85);
    transform-origin: center;
  }
}
</style>
